const SWITCHER_SELECTOR = '[data-gender-switcher]';
const SWITCHER_CONTAINER_SELECTOR = '[data-gender-switcher-container]';
const SWITCHER_ITEM_SELECTOR = '[data-gender-switcher-item]';
const SWITCHER_INDICATOR_SELECTOR = '[data-gender-switcher-indicator]';
const CONTROLLER_KEY = '__gldCollectionGenderSwitcherController';

const getMobileOS = (currentWindow) => {
  const navigator = currentWindow.navigator;
  const userAgent = navigator?.userAgent || '';

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  if (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator?.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'iOS';
  }

  return 'Other';
};

const debounce = (currentWindow, callback, wait) => {
  let timeout;

  return (...args) => {
    currentWindow.clearTimeout(timeout);
    timeout = currentWindow.setTimeout(() => callback(...args), wait);
  };
};

export const syncCollectionGenderSwitcherState = (
  switcher,
  activeItem,
  animate = true
) => {
  if (!switcher || !activeItem) {
    return false;
  }

  const indicator = switcher.querySelector(SWITCHER_INDICATOR_SELECTOR);
  const hasIndicator = Boolean(indicator);

  if (indicator) {
    if (!animate) {
      indicator.style.transition = 'none';
    }

    indicator.style.left = `${activeItem.offsetLeft}px`;
    indicator.style.width = `${activeItem.getBoundingClientRect().width}px`;

    if (!animate) {
      indicator.getBoundingClientRect();
      indicator.style.removeProperty('transition');
    }

    indicator.classList.remove('opacity-0');
  }

  switcher.querySelectorAll(SWITCHER_ITEM_SELECTOR).forEach((item) => {
    const isActive = item === activeItem;

    item.setAttribute('aria-checked', isActive ? 'true' : 'false');
    item.classList.toggle('text-gld-white', isActive);
    item.classList.toggle('text-gld-ink', !isActive);
    item.classList.toggle('bg-gld-ink', !hasIndicator && isActive);
    item.classList.toggle('bg-transparent', hasIndicator || !isActive);
  });

  return true;
};

/**
 * Initializes the collection gender switcher independently of collection
 * ownership. Constructor collections do not download `js_page-collection.js`,
 * while a later Shopify fallback does, so the controller is stored on window
 * to keep the delegated listener idempotent across both webpack runtimes.
 */
export const initCollectionGenderSwitcher = (
  currentDocument = typeof document !== 'undefined' ? document : null
) => {
  const currentWindow =
    currentDocument?.defaultView ||
    (typeof window !== 'undefined' ? window : null);

  if (!currentDocument || !currentWindow) {
    return null;
  }

  const existing = currentWindow[CONTROLLER_KEY];

  if (existing) {
    existing.sync();
    return existing;
  }

  let active = true;
  const sync = () => {
    if (!active) {
      return;
    }

    currentDocument.querySelectorAll(SWITCHER_SELECTOR).forEach((switcher) => {
      const activeItem = [
        ...switcher.querySelectorAll(SWITCHER_ITEM_SELECTOR),
      ].find((item) => item.dataset.value === switcher.dataset.active);

      syncCollectionGenderSwitcherState(switcher, activeItem, false);
    });
  };
  const onResize = debounce(currentWindow, sync, 100);
  const mobileOS = getMobileOS(currentWindow);
  const onClick = (event) => {
    const item = event.target.closest?.(SWITCHER_ITEM_SELECTOR);

    if (!item) {
      return;
    }

    const container = item.closest(SWITCHER_CONTAINER_SELECTOR);
    const switcher = item.closest(SWITCHER_SELECTOR);
    const itemValue =
      item.dataset.value || item.innerText?.trim().toLowerCase();
    const switcherUrl = item.dataset.url || '';

    if (
      !container ||
      !switcher ||
      !switcherUrl ||
      switcher.dataset.loading === 'true' ||
      itemValue === switcher.dataset.active
    ) {
      return;
    }

    event.preventDefault();

    const search = new currentWindow.URLSearchParams(
      currentDocument.location.search
    );
    search.delete('page');
    search.delete('limit');
    const query = search.toString();
    const url = `${switcherUrl}${
      query ? `${switcherUrl.includes('?') ? '&' : '?'}${query}` : ''
    }`;

    syncCollectionGenderSwitcherState(switcher, item);
    switcher.dataset.loading = 'true';
    switcher.dataset.active = itemValue;

    currentWindow.setTimeout(() => {
      currentDocument.location.href = url;
    }, mobileOS === 'iOS' ? 250 : 220);
  };
  const destroy = () => {
    if (!active) {
      return;
    }

    active = false;
    currentDocument.removeEventListener('click', onClick);
    currentWindow.removeEventListener('resize', onResize);
    currentDocument.removeEventListener('DOMContentLoaded', sync);
    delete currentWindow[CONTROLLER_KEY];
  };
  const controller = { destroy, sync };

  currentWindow[CONTROLLER_KEY] = controller;
  currentDocument.addEventListener('click', onClick);
  currentWindow.addEventListener('resize', onResize, { passive: true });

  if (currentDocument.readyState === 'loading') {
    currentDocument.addEventListener('DOMContentLoaded', sync, { once: true });
  }

  currentDocument.fonts?.ready?.then(sync);
  sync();

  return controller;
};

export default initCollectionGenderSwitcher;
